import Icon1 from "@assets/icons/Icon";
import { UserAvatar } from "@components/ui/UserAvatar";
import { MemberItem } from "@components/ui/blocks/MemberAssignmentBlock";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useDataStore } from "@store/user_org";
import HelpTooltip from "@components/ui/HelpTooltip";
import { useState, useEffect, useRef } from "react";
import { showNotification } from "context/notification.service";
import { ChangeUserPhotoModal } from "@components/modals/ChangeUserPhotoModal";
import { usersApi } from "@services/api/users";
import firebase from "@services/firebase";
import {
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";

const colorPalette = [
  "#A5B4FC", "#818CF8", "#C4B5FD", "#A78BFA", "#F9A8D4",
  "#FDA4AF", "#FCA5A5", "#FDBA74", "#FCD34D", "#FDE047",
  "#BEF264", "#86EFAC", "#6EE7B7", "#5EEAD4", "#67E8F9",
  "#7DD3FC", "#93C5FD", "#CBD5E1", "#94A3B8", "#D6D3D1",
  "#A8A29E",
];

export const Account = () => {
  const userData = useDataStore((state) => state.userData);
  const updateUserData = useDataStore((state) => state.updateUserData);
  const fetchUserData = useDataStore((state) => state.fetchUserData);

  const [displayName, setDisplayName] = useState(userData?.infos.display_name || "");
  const [fullName, setFullName] = useState(userData?.infos.full_name || "");
  const [phoneNumber, setPhoneNumber] = useState(userData?.infos.phone_number || "");
  const [colorTag, setColorTag] = useState(userData?.customization.color_tag || "#3B82F6");
  // Sync local state when userData loads or changes externally
  useEffect(() => {
    if (userData) {
      setDisplayName(userData.infos.display_name || "");
      setFullName(userData.infos.full_name || "");
      setPhoneNumber(userData.infos.phone_number || "");
      setColorTag(userData.customization.color_tag || "#3B82F6");
    }
  }, [userData?.id]);

  const [showColorPicker, setShowColorPicker] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [isUploadingSignature, setIsUploadingSignature] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const signatureFileInputRef = useRef<HTMLInputElement>(null);
  const [showSignatureCanvas, setShowSignatureCanvas] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const sigCanvasRef = useRef<HTMLCanvasElement>(null);
  const sigDrawingRef = useRef(false);

  const handleSignatureUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !userData) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      showNotification({ type: "error", message: "Format invalide. Acceptés : JPG, PNG, WEBP" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showNotification({ type: "error", message: "La taille dépasse 5MB" });
      return;
    }

    setIsUploadingSignature(true);
    try {
      await usersApi.uploadSignature(userData.id, file);
      await fetchUserData(userData.id);
      showNotification({ type: "success", message: "Signature enregistrée avec succès" });
    } catch {
      showNotification({ type: "error", message: "Échec du téléchargement de la signature" });
    } finally {
      setIsUploadingSignature(false);
      if (signatureFileInputRef.current) signatureFileInputRef.current.value = "";
    }
  };

  // Canvas drawing logic for hand-drawn signature
  useEffect(() => {
    if (!showSignatureCanvas) return;
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const getCoords = (clientX: number, clientY: number) => {
      const r = canvas.getBoundingClientRect();
      return {
        x: (clientX - r.left) * (canvas.width / r.width),
        y: (clientY - r.top) * (canvas.height / r.height),
      };
    };

    const startDraw = (x: number, y: number) => {
      sigDrawingRef.current = true;
      ctx.beginPath();
      ctx.moveTo(x, y);
    };
    const draw = (x: number, y: number) => {
      if (!sigDrawingRef.current) return;
      ctx.lineTo(x, y);
      ctx.strokeStyle = "#1e293b";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.stroke();
      setHasDrawn(true);
    };
    const stopDraw = () => { sigDrawingRef.current = false; };

    const onMouseDown = (e: MouseEvent) => { const { x, y } = getCoords(e.clientX, e.clientY); startDraw(x, y); };
    const onMouseMove = (e: MouseEvent) => { const { x, y } = getCoords(e.clientX, e.clientY); draw(x, y); };
    const onTouchStart = (e: TouchEvent) => { e.preventDefault(); const t = e.touches[0]; const { x, y } = getCoords(t.clientX, t.clientY); startDraw(x, y); };
    const onTouchMove = (e: TouchEvent) => { e.preventDefault(); const t = e.touches[0]; const { x, y } = getCoords(t.clientX, t.clientY); draw(x, y); };

    canvas.addEventListener("mousedown", onMouseDown);
    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("mouseup", stopDraw);
    canvas.addEventListener("mouseleave", stopDraw);
    canvas.addEventListener("touchstart", onTouchStart, { passive: false });
    canvas.addEventListener("touchmove", onTouchMove, { passive: false });
    canvas.addEventListener("touchend", stopDraw);

    return () => {
      canvas.removeEventListener("mousedown", onMouseDown);
      canvas.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("mouseup", stopDraw);
      canvas.removeEventListener("mouseleave", stopDraw);
      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchmove", onTouchMove);
      canvas.removeEventListener("touchend", stopDraw);
    };
  }, [showSignatureCanvas]);

  const clearSignatureCanvas = () => {
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const handleSaveDrawnSignature = async () => {
    const canvas = sigCanvasRef.current;
    if (!canvas || !userData) return;
    setIsUploadingSignature(true);
    try {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/png"),
      );
      if (!blob) throw new Error("Failed to export canvas");
      const file = new File([blob], "signature.png", { type: "image/png" });
      await usersApi.uploadSignature(userData.id, file);
      await fetchUserData(userData.id);
      showNotification({ type: "success", message: "Signature enregistrée avec succès" });
      setShowSignatureCanvas(false);
      setHasDrawn(false);
    } catch {
      showNotification({ type: "error", message: "Échec de l'enregistrement de la signature" });
    } finally {
      setIsUploadingSignature(false);
    }
  };

  const handleResetPassword = async () => {
    if (!userData?.id) return;
    setIsResettingPassword(true);
    try {
      await usersApi.sendPasswordReset(userData.id);
      showNotification({
        type: "success",
        message: "Un email de réinitialisation a été envoyé",
      });
    } catch (error: any) {
      const msg =
        error?.response?.data?.detail ||
        "Impossible d'envoyer l'email de réinitialisation";
      showNotification({ type: "error", message: msg });
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword || !currentPassword) {
      showNotification({ type: "error", message: "Veuillez remplir tous les champs" });
      return;
    }
    if (newPassword !== confirmPassword) {
      showNotification({ type: "error", message: "Les mots de passe ne correspondent pas" });
      return;
    }
    if (newPassword.length < 6) {
      showNotification({ type: "error", message: "Le mot de passe doit contenir au moins 6 caractères" });
      return;
    }

    const user = firebase.auth.currentUser;
    if (!user || !user.email) return;

    setIsChangingPassword(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      showNotification({ type: "success", message: "Mot de passe modifié avec succès" });
      setShowChangePassword(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      const code = error?.code;
      if (code === "auth/wrong-password" || code === "auth/invalid-credential") {
        showNotification({ type: "error", message: "Mot de passe actuel incorrect" });
      } else {
        showNotification({ type: "error", message: "Échec de la modification du mot de passe" });
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleSave = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!userData?.id) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      await updateUserData(userData.id, {
        infos: {
          email: userData.infos.email,
          display_name: displayName,
          full_name: fullName,
          phone_number: phoneNumber,
        },
        customization: {
          ...userData.customization,
          color_tag: colorTag,
        },
      });
      showNotification({
        type: "success",
        message: "Modifications enregistrées avec succès",
      });
    } catch (error: any) {
      console.error("Failed to update user:", error);
      const errorMessage = "Échec de l'enregistrement des modifications";
      setSaveError(errorMessage);
      showNotification({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-fit w-full max-w-500 pb-96 mt-10 mx-4 lg:ml-10">
      {showPhotoModal && (
        <ChangeUserPhotoModal onClose={() => setShowPhotoModal(false)} />
      )}
      <div className="flex flex-col  gap-4  ">
        <div className="flex flex-col   ">
          <Text1>Compte</Text1>
          <Text2>Configurez votre compte ici.</Text2>
          <Text3>
            Gérez vos informations personnelles, vos préférences et les
            paramètres de votre compte.
          </Text3>
        </div>
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h4 text-neutral-400">Photo de profil</div>
          <UserAvatar
            src={userData?.customization.photo_url}
            className="w-24 h-24 rounded-full border flex-shrink-0"
            value={userData?.customization.avatar_seed || userData?.infos.display_name}
            size={96}
          />
          <SecondaryButton
            startIcon={<Icon1 name="join" size={18}></Icon1>}
            label="Ajouter une photo"
            variant="large"
            onClick={() => setShowPhotoModal(true)}
          ></SecondaryButton>
        </div>
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h4 text-neutral-400">
            <div className="flex flex-row items-center gap-1.5">
              <span>Signature personnelle</span>
              <HelpTooltip content="Cette signature est automatiquement insérée dans les comptes-rendus et documents PDF que vous générez. Vous pouvez la redessiner ou téléverser une image." />
            </div>
          </div>
          <div className="text-h5 text-neutral-400">
            Votre signature sera automatiquement appliquée lors de la génération de comptes-rendus.
          </div>
          {userData?.infos.signature_id ? (
            <div className="border rounded-xl p-3 bg-neutral-50">
              <img
                src={userData.infos.signature_id}
                alt="Votre signature"
                className="max-h-24 object-contain"
              />
            </div>
          ) : (
            <div className="border-2 border-dashed border-neutral-200 rounded-xl p-6 flex items-center justify-center">
              <span className="text-h4 text-neutral-400">Aucune signature enregistrée</span>
            </div>
          )}
          <input
            ref={signatureFileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleSignatureUpload}
            className="hidden"
          />
          {showSignatureCanvas && (
            <div className="flex flex-col gap-2">
              <canvas
                ref={sigCanvasRef}
                width={600}
                height={200}
                className="border-2 border-dashed border-neutral-300 rounded-xl bg-white cursor-crosshair w-full"
                style={{ touchAction: "none" }}
              />
              <div className="flex flex-row gap-2">
                <SecondaryButton
                  variant="small"
                  onClick={clearSignatureCanvas}
                >
                  Effacer
                </SecondaryButton>
                <SecondaryButton
                  variant="small"
                  onClick={() => { setShowSignatureCanvas(false); setHasDrawn(false); }}
                >
                  Annuler
                </SecondaryButton>
                <Button
                  variant="small"
                  label={isUploadingSignature ? "Enregistrement..." : "Enregistrer la signature"}
                  onClick={handleSaveDrawnSignature}
                  disabled={!hasDrawn || isUploadingSignature}
                />
              </div>
            </div>
          )}
          <div className="flex flex-row gap-2">
            <SecondaryButton
              startIcon={<Icon1 name="join" size={18} />}
              label={
                isUploadingSignature
                  ? "Téléchargement..."
                  : userData?.infos.signature_id
                  ? "Importer une signature"
                  : "Importer une signature"
              }
              variant="large"
              disabled={isUploadingSignature || showSignatureCanvas}
              onClick={() => signatureFileInputRef.current?.click()}
            />
            <SecondaryButton
              startIcon={<Icon1 name="pinceau" size={18} />}
              label="Dessiner ma signature"
              variant="large"
              disabled={isUploadingSignature}
              onClick={() => { setShowSignatureCanvas(true); setHasDrawn(false); }}
            />
          </div>
        </div>
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <TextField
            label="Nom affiché"
            value={displayName}
            onChange={(value) => setDisplayName(value)}
            placeholder="Marie C."
          />
          <TextField
            label="Nom complet"
            value={fullName}
            onChange={(value) => setFullName(value)}
            placeholder="Marie Curie"
          />
          <TextField
            label="Adresse e-mail"
            placeholder="marie.curie@life.com"
            value={userData?.infos.email}
            disabled={true}
          />
          <TextField
            label="Numéro de téléphone"
            placeholder="06 12 34 56 78"
            type="phone"
            value={phoneNumber}
            onChange={(value) => setPhoneNumber(value)}
          />
          <TextField
            label="UID"
            placeholder="uid_1234567890"
            value={userData?.id}
            disabled={true}
          />
          <div className="text-h4 text-neutral-400 mt-4">Drapeau personnel</div>
          <MemberItem
            memberName={userData?.infos.display_name || "Utilisateur"}
            memberColor={colorTag}
          ></MemberItem>
          <SecondaryButton
            startIcon={<Icon1 name="pinceau" size={18}></Icon1>}
            label="Changer de couleur"
            variant="large"
            onClick={() => setShowColorPicker(!showColorPicker)}
          ></SecondaryButton>
          {showColorPicker && (
            <div className="flex flex-wrap gap-1 mt-2">
              {colorPalette.map((color) => (
                <button
                  key={color}
                  onClick={() => setColorTag(color)}
                  className={`w-8 h-8 rounded-full transition-all hover:scale-110 ${
                    colorTag === color
                      ? "ring-2 ring-offset-1 ring-black"
                      : "hover:ring-2 hover:ring-offset-1 hover:ring-neutral-300"
                  }`}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          )}
          {saveError && (
            <div className="text-h4 text-red-500 mt-2">{saveError}</div>
          )}
          <Button
            className="mt-4"
            label={isSaving ? "Enregistrement..." : "Enregistrer les modifications"}
            onClick={handleSave}
            disabled={isSaving}
          ></Button>
        </div>
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <SecondaryButton label="Modifier l'adresse email" />
          <SecondaryButton
            label={
              showChangePassword
                ? "Annuler la modification"
                : "Modifier le mot de passe"
            }
            onClick={() => {
              setShowChangePassword(!showChangePassword);
              setCurrentPassword("");
              setNewPassword("");
              setConfirmPassword("");
            }}
          />
          {showChangePassword && (
            <div className="flex flex-col gap-2 p-3 bg-neutral-50 rounded-lg">
              <TextField
                label="Mot de passe actuel"
                type="password"
                value={currentPassword}
                onChange={setCurrentPassword}
                placeholder="••••••••"
              />
              <TextField
                label="Nouveau mot de passe"
                type="password"
                value={newPassword}
                onChange={setNewPassword}
                placeholder="••••••••"
              />
              <TextField
                label="Confirmer le nouveau mot de passe"
                type="password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                placeholder="••••••••"
              />
              <Button
                label={isChangingPassword ? "Modification..." : "Confirmer"}
                onClick={handleChangePassword}
                disabled={isChangingPassword}
                variant="small"
              />
            </div>
          )}
          <SecondaryButton
            label={
              isResettingPassword
                ? "Envoi en cours..."
                : "Réinitialiser le mot de passe par email"
            }
            disabled={isResettingPassword}
            onClick={handleResetPassword}
          />
          <SecondaryButton
            danger={true}
            label="Supprimer le compte"
          />
        </div>
      </div>
    </div>
  );
};
